import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { FiMapPin, FiBriefcase, FiClock, FiArrowLeft, FiTag } from "react-icons/fi";
import { API_BASE_URL } from "../utils/constant";

// --- Helper Function ---
const getUpdatedText = (dateStr) => {
    if (!dateStr) return "Recently";
    try {
      const updatedDate = new Date(dateStr);
      return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(updatedDate);
    } catch (error) {
      return "Recently";
    }
};

// --- Skeleton Component ---
const JobDetailSkeleton = () => (
    <div className="bg-slate-50 min-h-screen animate-pulse">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="h-10 bg-slate-200 rounded w-3/4 mb-4"></div>
            <div className="h-6 bg-slate-200 rounded w-1/2 mb-12"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                <div className="lg:col-span-2 space-y-8">
                    <div className="h-8 bg-slate-200 rounded w-1/4 mb-4"></div>
                    <div className="space-y-3">
                        <div className="h-4 bg-slate-200 rounded"></div>
                        <div className="h-4 bg-slate-200 rounded w-5/6"></div>
                        <div className="h-4 bg-slate-200 rounded w-4/6"></div>
                    </div>
                </div>
                <div className="space-y-4">
                    <div className="h-40 bg-slate-200 rounded-xl"></div>
                </div>
            </div>
        </div>
    </div>
);

function JobDetailPage() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // --- Styles for raw HTML content (in-component) ---
  const jobContentStyles = `
    .job-content-wrapper h3 { font-size: 1.5rem; font-weight: 700; color: #1e293b; margin-top: 2rem; margin-bottom: 1rem; border-bottom: 1px solid #e2e8f0; padding-bottom: 0.5rem; }
    .job-content-wrapper h3:first-child { margin-top: 0; }
    .job-content-wrapper p { color: #475569; line-height: 1.75; margin-bottom: 1.25rem; }
    .job-content-wrapper ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 1.25rem; display: flex; flex-direction: column; gap: 0.75rem; }
    .job-content-wrapper li { color: #475569; }
    .job-content-wrapper a { color: #2563eb; font-weight: 600; text-decoration: underline; }
    .job-content-wrapper strong { font-weight: 600; color: #1e293b; }
  `;

  useEffect(() => {
    const fetchJob = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/jobs/${slug}`);
            if (!response.ok) throw new Error("Job not found");
            const data = await response.json();
            setJob({
                ...data,
                location: data.branch || 'Kolkata, WB',
                type: data.category || 'Full-time',
                skills: data.skills || [],
            });
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };
    fetchJob();
  }, [slug]);

  if (loading) return <JobDetailSkeleton />;
  if (!job) return (
    <div className="text-center py-20">
        <h2 className="text-2xl font-bold text-slate-800">Job Not Found</h2>
        <p className="text-slate-600 mt-2">The position you are looking for does not exist or may have been filled.</p>
        <Link to="/career" className="inline-flex items-center mt-6 bg-blue-600 text-white font-semibold py-2 px-5 rounded-lg shadow-sm hover:bg-blue-700 transition-colors">
            <FiArrowLeft className="mr-2" />
            Back to Careers
        </Link>
    </div>
  );

  const isAvailable = job.status === "active" || job.status === "Available";

  return (
    <>
      <style>{jobContentStyles}</style>

      <Helmet>
        <title>{job.seo_title || job.title} | Alsol Technology</title>
        {job.seo_description && <meta name="description" content={job.seo_description} />}
      </Helmet>

      <main className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          {/* --- Restored Previous Header Style --- */}
          <motion.div 
            className="mb-12"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <Link to="/career" className="inline-flex items-center text-sm text-blue-600 font-semibold hover:underline mb-4">
              <FiArrowLeft className="mr-2"/> Back to all openings
            </Link>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">{job.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-slate-600">
                <span className="flex items-center gap-2"><FiMapPin /> {job.location}</span>
                <span className="flex items-center gap-2"><FiBriefcase /> {job.type}</span>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            {/* --- Left Column: Main Content --- */}
            <motion.div 
              className="lg:col-span-2 bg-white rounded-xl border border-slate-200/80 p-6 sm:p-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <div className="job-content-wrapper">
                {job.description && (
                  <div>
                    <h3 id="job-description">About the Role</h3>
                    <div dangerouslySetInnerHTML={{ __html: job.description }} />
                  </div>
                )}
                 {job.skills && job.skills.length > 0 && (
                    <div>
                        <h3>Skills Required</h3>
                        <div className="flex flex-wrap gap-2">
                            {job.skills.map(skill => (
                                <span key={skill.id} className="bg-slate-100 text-slate-700 text-sm font-medium px-3 py-1.5 rounded-md">
                                    {skill.name}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {job.requirement && (
                  <div>
                    <h3>Qualifications & Requirements</h3>
                    <div dangerouslySetInnerHTML={{ __html: job.requirement }} />
                  </div>
                )}
              </div>
            </motion.div>

            {/* --- Right Sidebar with Sticky Positioning --- */}
            <motion.div 
              className="lg:sticky top-24" // This makes the sidebar sticky
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              <div className="bg-white rounded-xl border border-slate-200/80 shadow-lg">
                <div className="p-6">
                    <button
                        onClick={() => navigate("/career/apply", { state: { job } })}
                        disabled={!isAvailable}
                        className={`w-full px-8 py-3 font-bold text-lg rounded-lg text-white transition-all duration-300 transform hover:scale-105 ${
                            isAvailable
                            ? "bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/20"
                            : "bg-slate-400 cursor-not-allowed"
                        }`}
                    >
                        Apply Now
                    </button>
                </div>
                <div className="border-t border-slate-200 p-6 space-y-4">
                    <h4 className="text-lg font-semibold text-slate-800">Job Overview</h4>
                    <ul className="space-y-3 text-slate-600">
                        <li className="flex items-center gap-3">
                            <FiClock className="text-slate-400 flex-shrink-0" />
                            <span><strong>Posted on:</strong> {getUpdatedText(job.start_date)}</span>
                        </li>
                        <li className="flex items-center gap-3">
                            <FiMapPin className="text-slate-400 flex-shrink-0" />
                            <span><strong>Location:</strong> {job.location}</span>
                        </li>
                        <li className="flex items-center gap-3">
                            <FiBriefcase className="text-slate-400 flex-shrink-0" />
                            <span><strong>Job Type:</strong> {job.type}</span>
                        </li>
                         <li className="flex items-center gap-3">
                            <FiTag className="text-slate-400 flex-shrink-0" />
                            <span><strong>Status:</strong> <span className={`font-semibold ${isAvailable ? 'text-green-600' : 'text-slate-500'}`}>{isAvailable ? 'Open' : 'Closed'}</span></span>
                        </li>
                    </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </>
  );
}

export default JobDetailPage;